import _ from 'lodash';
import React, { RefObject, useEffect } from 'react';
import { Alert, Button, Col, Grid, Row } from 'react-bootstrap';
import {
  FinanceResponse,
  FinanceResponseData,
} from '@upstart/harmony/lib/legos/financingTypes';
import {
  isEmbeddedInClosingWorkflow,
  isEmbeddedInCommandCenter,
} from 'util/commandCenter';
import { sendData } from 'util/amplitude';
import { amplitudeEvents } from 'util/amplitudeEvents';
import { isLendingStateCode } from 'hooks/services';
import { useRicStateSchemaContent } from 'hooks/api/ricStateSchemas';
import LoadingOverlay from '../../../components/LoadingOverlay';
import { RicFormSchema } from './ricFormTypes';
import SchemaBasedRICForm from './SchemaBasedRicForm';

const IS_EMBEDDED_IN_CC = isEmbeddedInCommandCenter();

const SAVE_BUTTON_TEXT = IS_EMBEDDED_IN_CC
  ? 'Save and start eContracting'
  : 'Save and Start eContracting';

const CANCEL_BUTTON_TEXT = IS_EMBEDDED_IN_CC
  ? 'Cancel and revert changes'
  : 'Cancel and Revert Changes';

export type RicSchemaAvailability = 'available' | 'loading' | 'failed';

export function RICForm(props: {
  response: FinanceResponse;
  fetchResponseWithUpdatedData: (data: Partial<FinanceResponseData>) => void;
  onCancel: () => void;
  loading: boolean;
  fetchingError: string | null;
  callReviewFinancing: () => Promise<void>;
  state?: string | null;
  formRef?: RefObject<HTMLFormElement>;
  onSchemaAvailabilityChange?: (availability: RicSchemaAvailability) => void;
}) {
  const {
    response,
    fetchResponseWithUpdatedData,
    callReviewFinancing,
    fetchingError,
    state,
    formRef,
    onSchemaAvailabilityChange,
  } = props;

  const { hasDuplicateTradeIn, isInvalid } = response.validations ?? {};
  const responseData = response.data ?? {};

  const fetchingErrorAlert = <Alert bsStyle="danger">{fetchingError}</Alert>;

  // Unsupported states are treated as empty forms.
  const stateCode = state && isLendingStateCode(state) ? state : undefined;

  const { schemaContent, isLoadingSchemaContent, schemaContentError } =
    useRicStateSchemaContent(stateCode);

  const schemaAvailability = ((): RicSchemaAvailability => {
    if (schemaContentError) {
      return 'failed';
    }
    return isLoadingSchemaContent ? 'loading' : 'available';
  })();

  const schemaUnavailable = schemaAvailability !== 'available';

  useEffect(() => {
    onSchemaAvailabilityChange?.(schemaAvailability);
  }, [schemaAvailability, onSchemaAvailabilityChange]);

  useEffect(() => {
    sendData(amplitudeEvents.DESKING_EDIT_RIC_START);
  }, []);

  const getForm = () => {
    if (schemaContentError) {
      return (
        <Alert bsStyle="danger">
          The contract form for {state} could not be loaded:{' '}
          {schemaContentError.message}. Reload the page to try again — do not
          start eContracting until the form renders.
        </Alert>
      );
    }

    if (isLoadingSchemaContent) {
      return <Alert>Loading the contract form...</Alert>;
    }

    return (
      <SchemaBasedRICForm
        responseData={responseData}
        fetchResponseWithUpdatedData={fetchResponseWithUpdatedData}
        // The backend API's OpenAPI-generated wire type necessarily widens
        // Harmony-derived `keyof`/enum fields to plain strings; this schema is
        // otherwise identical JSON.
        schema={schemaContent as unknown as RicFormSchema | undefined}
      />
    );
  };

  const onFormSubmit = (event: any) => {
    event.preventDefault();
    callReviewFinancing();
  };

  return (
    <form
      onSubmit={onFormSubmit}
      data-dd-privacy="allow"
      ref={formRef}
      data-testid="ric-form"
    >
      <div>
        {!isEmbeddedInClosingWorkflow() && (
          <div className="flex">
            <h3 className="pr-15">Retail Installment Sale Contract</h3>
          </div>
        )}
        <LoadingOverlay
          size="small"
          className="bottom-loading-indicator"
          active={props.loading}
          inline={false}
        />
        <div
          className="upstart-offer-container"
          style={
            isEmbeddedInClosingWorkflow()
              ? { padding: 0, boxShadow: 'none' }
              : {}
          }
        >
          <Grid>
            <Row>
              <Col sm={12}>
                {!_.isEmpty(fetchingError) && fetchingErrorAlert}
              </Col>
            </Row>
            <Row>
              <Col md={12} lg={10}>
                <div className="form-container">{getForm()}</div>
              </Col>
            </Row>
            {!isEmbeddedInClosingWorkflow() && (
              <Row>
                <Col md={6} xs={12} className="button-footer">
                  <Button className="cancel-button" onClick={props.onCancel}>
                    {CANCEL_BUTTON_TEXT}
                  </Button>
                  <Button
                    data-testid="ric-form-submit-button"
                    type="submit"
                    bsStyle="primary"
                    className="submit-button"
                    disabled={
                      props.loading ||
                      isInvalid ||
                      hasDuplicateTradeIn ||
                      schemaUnavailable
                    }
                  >
                    {SAVE_BUTTON_TEXT}
                  </Button>
                </Col>
              </Row>
            )}
          </Grid>
        </div>
      </div>
    </form>
  );
}

export default RICForm;
